import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Archive,
  ExternalLink,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useOfficeDocument } from '../../hooks/useOfficeDocument'
import { uploadDocument, updateDocumentMetadata } from '../../services/documentsApi'
import { addTagToDocument } from '../../services/tagsApi'
import { readContentControls, ccValuesToMetadata, mergeMetadata } from '../../hooks/useContentControls'
import { writeXmlMetadata, writeDocumentBinding } from '../../hooks/useCustomXmlParts'
import { devLog } from '../../services/devLog'
import { MetadataPanel } from '../metadata/MetadataPanel'
import { TagPicker } from '../organize/TagPicker'
import { AnimatedCheckmark } from '../common/AnimatedCheckmark'
import { ContextualTip, markActionCompleted } from '../common/ContextualTip'
import { MAIN_APP_URL } from '../../config'
import type { DocumentMetadata } from '../metadata/MetadataPanel'
import type { Actor } from '../../hooks/useActors'

interface ArchiveDocumentProps {
  actorId?: string
  actors?: Actor[]
  onActorChange?: (actorId: string) => void
}

/**
 * "Save to KobSign" flow — upload document without signing.
 *
 * Simpler than SendDocument: title + metadata + tags → upload → done.
 * No signers, no invitations. Document is stored as draft in KobSign.
 */
export function ArchiveDocument({ actorId, actors }: ArchiveDocumentProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { getDocumentBlob, isReading } = useOfficeDocument()

  const [title, setTitle] = useState('')
  const [metadata, setMetadata] = useState<DocumentMetadata>({})
  const [selectedTags, setSelectedTags] = useState<number[]>([])
  const [isMetadataExpanded, setIsMetadataExpanded] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [savedKoblinkId, setSavedKoblinkId] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const activeActor = actors?.find((a) => a.koblink_id === actorId)
  const orgKoblinkId = activeActor?.actor_type === 'ORGANIZATION' ? actorId : undefined

  const hasTitle = title.trim().length > 0
  const canSave = hasTitle && !isSaving && !isReading

  const handleSave = async () => {
    setErrorMessage(null)
    setIsSaving(true)

    try {
      devLog.info('Archive', `Saving "${title.trim()}" for actor=${actorId || 'default'}`)

      // Step 1: Get DOCX from Word
      const blob = await getDocumentBlob()
      const filename = `${title.trim()}.docx`

      // Step 2: Upload document (creates draft)
      devLog.info('Archive 1/3', 'Uploading DOCX...')
      const doc = await uploadDocument(blob, filename, title.trim(), actorId)
      devLog.info('Archive 1/3 OK', `koblink_id=${doc.koblink_id}`)

      // Step 3: Read Content Controls and merge with panel metadata
      let finalMetadata = metadata
      try {
        const ccValues = await readContentControls()
        const ccMetadata = ccValuesToMetadata(ccValues)
        if (Object.keys(ccMetadata).length > 0) {
          finalMetadata = mergeMetadata(metadata, ccMetadata)
          devLog.info('Archive CC', `Merged ${Object.keys(ccMetadata).length} CC field(s)`)
        }
      } catch (e) {
        devLog.warn('Archive CC', `Failed to read CCs: ${e}`)
      }

      // Write metadata as Custom XML Part in DOCX (best-effort)
      // Always include owner_actor_id so auto-switch works on reopen
      try {
        const xmlData: Record<string, string> = {}
        if (finalMetadata.counterparties?.length) xmlData.counterparty = finalMetadata.counterparties.join(', ')
        if (finalMetadata.contract_value) xmlData.contract_value = finalMetadata.contract_value
        if (finalMetadata.contract_currency) xmlData.contract_currency = finalMetadata.contract_currency
        if (finalMetadata.contract_expires_at) xmlData.contract_expires_at = finalMetadata.contract_expires_at
        if (actorId) xmlData.owner_actor_id = actorId
        if (Object.keys(xmlData).length > 0) {
          await writeXmlMetadata(xmlData)
        }
      } catch (e) {
        devLog.warn('Archive XML', `Write failed (non-blocking): ${e}`)
      }

      // Write document binding so panel detects this doc on next open
      try {
        await writeDocumentBinding({
          koblink_id: doc.koblink_id,
          title: title.trim(),
          status: 'draft',
          bound_at: new Date().toISOString(),
          owner_actor_id: actorId,
        })
      } catch (e) {
        devLog.warn('Archive Binding', `Write failed (non-blocking): ${e}`)
      }

      // Apply metadata (non-blocking)
      const metadataPayload: Record<string, unknown> = {}
      if (finalMetadata.folder_id != null) metadataPayload.folder = finalMetadata.folder_id
      if (finalMetadata.document_type_id != null) metadataPayload.document_type_id = finalMetadata.document_type_id
      if (finalMetadata.department_id != null) metadataPayload.department = finalMetadata.department_id
      if (finalMetadata.responsible_person) metadataPayload.responsible_person = finalMetadata.responsible_person
      if (finalMetadata.contract_value) metadataPayload.contract_value = finalMetadata.contract_value
      if (finalMetadata.contract_currency) metadataPayload.contract_currency = finalMetadata.contract_currency
      if (finalMetadata.contract_expires_at) metadataPayload.contract_expires_at = finalMetadata.contract_expires_at
      if (finalMetadata.counterparties && finalMetadata.counterparties.length > 0) metadataPayload.counterparties = finalMetadata.counterparties

      if (Object.keys(metadataPayload).length > 0) {
        try {
          devLog.info('Archive 2/3', `Setting ${Object.keys(metadataPayload).length} metadata field(s)...`)
          await updateDocumentMetadata(doc.koblink_id, metadataPayload, actorId)
          devLog.info('Archive 2/3 OK', 'Metadata saved')
        } catch (e) {
          devLog.warn('Archive 2/3', `Metadata failed (non-blocking): ${e}`)
        }
      }

      // Step 4: Add tags (non-blocking)
      if (selectedTags.length > 0) {
        devLog.info('Archive 3/3', `Adding ${selectedTags.length} tag(s)...`)
        for (const tagId of selectedTags) {
          try {
            await addTagToDocument(doc.koblink_id, tagId, actorId)
          } catch (e) {
            devLog.warn('Archive 3/3', `Tag ${tagId} failed: ${e}`)
          }
        }
        devLog.info('Archive 3/3 OK', 'Tags added')
      }

      // Invalidate document list
      queryClient.invalidateQueries({ queryKey: ['documents'] })

      setSavedKoblinkId(doc.koblink_id)
      setSaved(true)
      devLog.info('Archive', `Done — ${doc.koblink_id}`)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed'
      setErrorMessage(msg)
      devLog.error('Archive', msg)
    } finally {
      setIsSaving(false)
    }
  }

  const resetForm = () => {
    setSaved(false)
    setSavedKoblinkId(null)
    setTitle('')
    setMetadata({})
    setSelectedTags([])
    setIsMetadataExpanded(true)
    setErrorMessage(null)
  }

  // Success state
  if (saved) {
    markActionCompleted('first-archive')
    return (
      <div className="flex flex-col items-center justify-center flex-1 px-6 py-8 text-center animate-fade-in">
        <AnimatedCheckmark size={48} className="mb-5" />
        <h2 className="text-[15px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100 mb-1">
          {t('archive.saved')}
        </h2>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 max-w-[220px]">
          {t('archive.savedDescription')}
        </p>
        {savedKoblinkId && (
          <a
            href={`${MAIN_APP_URL}/documents/${savedKoblinkId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
          >
            <ExternalLink size={12} />
            {t('status.viewInKobSign')}
          </a>
        )}
        <button
          onClick={resetForm}
          className="mt-5 px-4 py-2 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 active:scale-[0.98] transition-all"
        >
          {t('archive.saveAnother')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 px-3 py-3">
      <h2 className="text-[15px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100 mb-1">
        {t('archive.title')}
      </h2>
      <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mb-2">
        {t('archive.description')}
      </p>

      <div className="mb-3">
        <ContextualTip context="first-archive">
          {t('tips.firstArchive')}
        </ContextualTip>
      </div>

      {/* Document title */}
      <div className="mb-3">
        <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5 block">
          {t('sign.documentTitle')}
        </label>
        <input
          type="text"
          placeholder={t('sign.documentTitlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
        />
      </div>

      {/* Metadata panel (expanded by default for archive) */}
      <MetadataPanel
        actorId={actorId}
        orgKoblinkId={orgKoblinkId}
        isExpanded={isMetadataExpanded}
        onToggle={() => setIsMetadataExpanded(!isMetadataExpanded)}
        metadata={metadata}
        onMetadataChange={setMetadata}
      />

      {/* Tags */}
      <div className="mb-3">
        <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5 block">
          {t('organize.tags')}
        </label>
        <TagPicker
          actorId={actorId}
          selectedTagIds={selectedTags}
          onToggle={(tagId) => {
            setSelectedTags((prev) =>
              prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
            )
          }}
        />
      </div>

      {/* Error */}
      {errorMessage && (
        <p className="text-xs text-error-500 mb-3">
          {errorMessage}
        </p>
      )}

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={!canSave}
        className="mt-auto flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium active:scale-[0.98] shadow-sm hover:shadow-md transition-all"
      >
        {isReading || isSaving ? (
          <>{isReading ? t('sign.uploadingDocument') : t('archive.saving')}</>
        ) : (
          <>
            <Archive size={14} />
            {t('archive.saveButton')}
          </>
        )}
      </button>
    </div>
  )
}
